import { FormFieldResponseDTO, FormFieldLookupTypeDTO } from '@/api/base/DTOs';
import { IFormBase, IFormSelect } from '@/forms/typings';
import { SelectFormFieldBuilder } from '@/forms/builders';
import { store } from '@/store';
import { setExternalLookupOptions } from '@/store/slices';
import { ADDRESS_LOOKUP_TYPES } from '@/forms/utils/getTypeaheadHintText';
import { allowsPoBox } from '@/forms/utils/poBox';
import { adaptFormFieldResponseToValue } from '../utilities/formFieldResponseToValue.adapter';
import { adaptInternalLookupResponseToOptions } from '../utilities/internalLookupResponseToOptions.adapter';
import { adaptLookupEntryAddresses } from '../utilities/adaptLookupEntryAddresses';
import { adaptFormFieldExternalLookupResponseToExternalLookupOptions } from '../utilities/formFieldExternalLookupResponseToExternalLookupOptions.adapter';
import { formApiAdapterRegistry } from '../registry/form-api-adapter.registry';

/** The stored name when the server didn't flag it as typed; null for an empty or typed value. */
const storedRegistryName = (
  formFieldResponse: FormFieldResponseDTO,
): string | null => {
  const stored = formFieldResponse.values?.[0];
  const name = stored?.value ?? '';
  return name.trim() !== '' && stored?.notFromRegistry !== true ? name : null;
};

/**
 * Adapts a FormFieldResponseDTO to a SelectFormField
 * Handles fields with internal or external lookups configured as select/dropdown
 */
export const adaptFormFieldResponseToSelectFormField = (
  formFieldResponse: FormFieldResponseDTO,
  parentFieldId?: IFormBase['fieldId'],
): IFormSelect => {
  const externalLookupOptions =
    formFieldResponse.lookupType === FormFieldLookupTypeDTO.External &&
    formFieldResponse.externalLookup !== undefined
      ? adaptFormFieldExternalLookupResponseToExternalLookupOptions(
          formFieldResponse.externalLookup,
        )
      : undefined;

  // Handle external lookup options dispatch to store
  if (externalLookupOptions) {
    store.dispatch(
      setExternalLookupOptions({
        fieldName: formFieldResponse.fieldID,
        options: externalLookupOptions,
      }),
    );
  }

  let selectFormField = new SelectFormFieldBuilder()
    .fieldId(formFieldResponse.fieldID)
    .name(formFieldResponse.fieldID)
    .label(formFieldResponse.label ?? formFieldResponse.name)
    .readonly(formFieldResponse.readonly || !formFieldResponse.isEditable)
    .value(adaptFormFieldResponseToValue(formFieldResponse))
    .hasAnyValue(formFieldResponse.hasAnyValue)
    .notApplicable(formFieldResponse.notApplicable)
    .showNotApplicable(formFieldResponse.showNotApplicable)
    .size(formFieldResponse.itemSize)
    .useShortName(formFieldResponse.internalLookup?.useShortName ?? false)
    .options(
      formFieldResponse.internalLookup
        ? adaptInternalLookupResponseToOptions(
            formFieldResponse.internalLookup,
            formFieldResponse.values,
          )
        : undefined,
    )
    .canAddEntry(formFieldResponse.internalLookup?.canAddEntry)
    .multiple(formFieldResponse.restriction?.allowMultipleValues)
    .lookupEntryAddresses(
      adaptLookupEntryAddresses(
        formFieldResponse.lookupEntryAddresses,
        formFieldResponse.internalLookup,
      ),
    )
    .rejectsPoBox(
      externalLookupOptions !== undefined &&
        ADDRESS_LOOKUP_TYPES.has(externalLookupOptions.lookupType) &&
        !allowsPoBox(externalLookupOptions.additionalLookupArguments),
    )
    .registryName(storedRegistryName(formFieldResponse));

  if (formFieldResponse.hint) {
    selectFormField = selectFormField.hint(formFieldResponse.hint);
  }

  if (formFieldResponse.hintImageUrl) {
    selectFormField = selectFormField.hintImageUrl(
      formFieldResponse.hintImageUrl,
    );
  }

  const selectFieldValidations =
    formApiAdapterRegistry.adaptGeneric(
      formFieldResponse.restriction,
    ) as IFormSelect['validations'];

  if (selectFieldValidations) {
    selectFormField = selectFormField.validations(selectFieldValidations);
  }

  return selectFormField.parentFieldId(parentFieldId).build();
};
